import { DEDUPE_ACTIONS, DEDUPE_SCOPES, DEDUPE_STRATEGIES, type DedupeRule } from "@sga/shared";

/**
 * One duplicate-prevention rule, as a form.
 *
 * The wording matters more than usual here. A rule decides whether a real applicant is turned away
 * at submit, so each choice says what it will do to a person rather than naming the enum value the
 * database stores.
 */

export interface FieldChoice {
  key: string;
  label: string;
  type: string;
}

const STRATEGY_LABELS: Record<(typeof DEDUPE_STRATEGIES)[number], string> = {
  one_per_respondent_per_cycle: "One submission per applicant account",
  composite_key: "These answers must be unique together",
  phone_unique: "This phone number must be unique",
};

const SCOPE_LABELS: Record<(typeof DEDUPE_SCOPES)[number], string> = {
  form_cycle: "this form, this cycle",
  form_all_cycles: "this form, every cycle",
  programme_cycle: "every form in the programme, this cycle",
  global_cycle: "every form anywhere, this cycle",
};

const ACTION_LABELS: Record<(typeof DEDUPE_ACTIONS)[number], string> = {
  block: "Refuse the submission",
  warn_and_allow: "Warn, but let it through",
  replace_previous: "Replace the earlier submission",
};

const ACTION_NOTES: Record<(typeof DEDUPE_ACTIONS)[number], string> = {
  block:
    "The only action backed by a database constraint. The others are advisory — two browser tabs on deadline day can still produce two rows.",
  warn_and_allow: "The applicant sees the message and can continue. Nothing is prevented.",
  replace_previous: "The earlier submission is marked superseded and drops out of exports.",
};

export const DedupeRuleCard = ({
  rule,
  index,
  total,
  fields,
  conflicts,
  onChange,
  onMove,
  onRemove,
}: {
  rule: DedupeRule;
  index: number;
  total: number;
  fields: FieldChoice[];
  conflicts: number | undefined;
  onChange: (next: DedupeRule) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}): JSX.Element => {
  const needsFields = rule.strategy === "composite_key" || rule.strategy === "phone_unique";
  const single = rule.strategy === "phone_unique";
  const choices = single ? fields.filter((field) => field.type === "phone") : fields;

  /**
   * A composite-key rule is enforced by the unique index on `submission_dedupe_keys`, and that has
   * two consequences the coordinator has to see rather than discover:
   *
   *  - it can only refuse, because the index is the block (see the schema's superRefine);
   *  - the lookup is by rule, not by cycle, so the scope selector would be decoration.
   *
   * Offering either control here would let someone configure a rule that reads correctly on screen
   * and enforces something else.
   */
  const keyedOnAnswers = rule.strategy === "composite_key";
  const actionChoices = keyedOnAnswers ? (["block"] as const) : DEDUPE_ACTIONS;

  const toggleField = (key: string): void => {
    if (single) {
      onChange({ ...rule, fieldKeys: [key] });
      return;
    }
    const next = rule.fieldKeys.includes(key)
      ? rule.fieldKeys.filter((entry) => entry !== key)
      : [...rule.fieldKeys, key];
    onChange({ ...rule, fieldKeys: next });
  };

  return (
    <li className={`rounded-md border p-3 ${rule.isActive ? "" : "opacity-60"}`}>
      <div className="mb-2 flex items-center gap-2">
        <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
          {index + 1}
        </span>
        <select
          aria-label="Rule"
          className="flex-1 rounded-md border px-2 py-1.5 text-sm"
          value={rule.strategy}
          onChange={(event) => {
            const strategy = event.target.value as DedupeRule["strategy"];
            onChange({
              ...rule,
              strategy,
              // The field list means something different per strategy, and carrying a composite
              // key's four fields into a phone rule would save a rule nobody described.
              fieldKeys: [],
              // Composite keys can only refuse; carrying "warn" across from another strategy would
              // save a rule the server rejects, or worse, one that silently never fires.
              action: strategy === "composite_key" ? "block" : rule.action,
            });
          }}
        >
          {DEDUPE_STRATEGIES.map((strategy) => (
            <option key={strategy} value={strategy}>
              {STRATEGY_LABELS[strategy]}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="rounded border px-2 py-1 text-xs disabled:opacity-40"
          onClick={() => onMove(-1)}
          disabled={index === 0}
          title="Rules are evaluated top to bottom; the first match wins"
        >
          ↑
        </button>
        <button
          type="button"
          className="rounded border px-2 py-1 text-xs disabled:opacity-40"
          onClick={() => onMove(1)}
          disabled={index === total - 1}
        >
          ↓
        </button>
        <button
          type="button"
          className="rounded border px-2 py-1 text-xs text-brand-red hover:bg-brand-red/10"
          onClick={onRemove}
        >
          Remove
        </button>
      </div>

      {needsFields && (
        <fieldset className="mb-2">
          <legend className="mb-1 text-xs uppercase text-muted-foreground">
            {single ? "Phone field" : "Answers that identify one person"}
          </legend>
          {choices.length === 0 ? (
            <p className="text-xs text-brand-red">
              This form has no {single ? "phone" : "usable"} field to key on.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {choices.map((field) => (
                <label
                  key={field.key}
                  className={`cursor-pointer rounded-full border px-2 py-0.5 text-xs ${
                    rule.fieldKeys.includes(field.key)
                      ? "border-primary bg-primary/10 text-primary"
                      : "hover:bg-muted"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={rule.fieldKeys.includes(field.key)}
                    onChange={() => toggleField(field.key)}
                  />
                  {field.label}
                </label>
              ))}
            </div>
          )}
        </fieldset>
      )}

      <div className="mb-2 flex flex-wrap gap-2">
        {keyedOnAnswers ? (
          <p className="max-w-xs text-xs text-muted-foreground">
            <span className="mb-1 block uppercase">Looks across</span>
            this form, every cycle — a rule matching on answers is enforced by an index keyed to the
            rule itself, so it cannot be narrowed to one cycle.
          </p>
        ) : (
          <label className="text-xs">
            <span className="mb-1 block uppercase text-muted-foreground">Look across</span>
            <select
              className="rounded-md border px-2 py-1.5 text-sm"
              value={rule.scope}
              onChange={(event) =>
                onChange({ ...rule, scope: event.target.value as DedupeRule["scope"] })
              }
            >
              {DEDUPE_SCOPES.map((scope) => (
                <option key={scope} value={scope}>
                  {SCOPE_LABELS[scope]}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="text-xs">
          <span className="mb-1 block uppercase text-muted-foreground">When it matches</span>
          <select
            className="rounded-md border px-2 py-1.5 text-sm disabled:opacity-60"
            value={rule.action}
            disabled={keyedOnAnswers}
            title={
              keyedOnAnswers
                ? "A rule matching on answers is enforced by a database constraint, which can only refuse."
                : undefined
            }
            onChange={(event) =>
              onChange({ ...rule, action: event.target.value as DedupeRule["action"] })
            }
          >
            {actionChoices.map((action) => (
              <option key={action} value={action}>
                {ACTION_LABELS[action]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-end gap-1 text-xs">
          <input
            type="checkbox"
            checked={rule.isActive}
            onChange={(event) => onChange({ ...rule, isActive: event.target.checked })}
          />
          <span className="pb-2">Active</span>
        </label>
      </div>

      <p className="mb-2 text-xs text-muted-foreground">{ACTION_NOTES[rule.action]}</p>

      <label className="block">
        <span className="mb-1 block text-xs uppercase text-muted-foreground">
          Message the applicant sees
        </span>
        <input
          className="w-full rounded-md border px-2 py-1.5 text-sm"
          value={rule.message ?? ""}
          placeholder="You have already applied to this programme this year."
          onChange={(event) => onChange({ ...rule, message: event.target.value || null })}
        />
      </label>

      {conflicts !== undefined && conflicts > 0 && (
        <p role="status" className="mt-2 rounded bg-brand-red/10 p-2 text-xs text-brand-red">
          {conflicts.toLocaleString()} submission{conflicts === 1 ? "" : "s"} already in this form
          could not be protected: an earlier row holds the same key. This rule guards everything
          submitted from now on, but those duplicates are already here and need resolving by hand.
        </p>
      )}
    </li>
  );
};
